"use client";
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MEDIA_ASSETS } from "@/lib/media-assets";
import KineticText from "./KineticText";

/**
 * CosmicJourney — looping color-changing sphere hero.
 *
 * History: this hero was a ~23 MB Spline WebGL galaxy, then a 101-frame WebP
 * <canvas> sequence, then a one-shot pre-rendered cinematic clip. It now loops
 * the owner's "magic spheres" montage through a <video> element, with the page
 * chrome reacting to the sphere's changing color:
 *
 *   • The sphere clip LOOPS continuously while the hero is on screen.
 *   • A tiny hidden canvas samples the video a few times a second and takes a
 *     luminance-weighted average that ignores the black background and locks
 *     onto the vivid orb color. That color drives a single CSS variable
 *     (`--glow`) — no React re-renders — which tints an ambient glow behind the
 *     orb and the soft blurred screen edges.
 *   • The WHITE welcome text sits over a soft dark scrim + text-shadow so it
 *     stays legible no matter what color the sphere becomes.
 *   • Hand-off: the hero <-> #paths transition AUTO-COMMITS on BOTH desktop and
 *     mobile via the same scroll-triggered handoff (down -> #paths, up -> top);
 *     desktop adds precise wheel/keyboard triggers on top.
 *   • iOS: the desktop colored edge/corner glow uses filter:blur + mix-blend
 *     screen; blur is fine, but mix-blend-mode:screen paints black boxes on iOS
 *     WebKit, so on phones (globals.css @media) the SAME edge/corner glow is kept
 *     with the blend mode neutralised to normal (screen ~= normal on the
 *     near-black hero backdrop), so mobile edges match desktop.
 *   • The video AND the color sampling both stop when the hero leaves the
 *     viewport (no idle work); prefers-reduced-motion shows a static frame
 *     with no playback, no sampling, and no listeners.
 */

// H.264 MP4 is hardware-decoded in every browser → smooth playback.
const VIDEO_SRC_MP4 = MEDIA_ASSETS.sphereMontage.url;

// Color-matched glow (desktop AND mobile) via a DUAL-BUFFER opacity crossfade.
// The video is sampled a few times a second; its dominant color is committed to
// one of two stacked copies (A/B) of every glow layer. We paint the new color
// into the CURRENTLY-HIDDEN buffer (opacity 0 → its blur rasterizes once,
// off-screen), then crossfade by animating OPACITY only — which the compositor
// does cheaply — so the active buffer fades out as the new one fades in.
//
// Why this shape: an OLD build smoothed the color with `transition: background
// 0.25s` on each layer, but --glow was rewritten (~11 Hz) FASTER than that
// 250ms transition finished, so the transition perpetually restarted and the
// browser re-rasterized three large filter:blur() layers at ~60 Hz nonstop —
// the confirmed desktop stutter and the phone scroll-jank ("retint storm"). A
// no-transition build was smooth but the color jumps looked too sharp. The
// dual-buffer crossfade keeps the smooth, premium blend WITHOUT the storm: the
// blur is re-rastered at most ~3×/s (once per committed color), never per frame,
// so the live glow is cheap enough to run on mobile too.
const ENABLE_GLOW_SAMPLING = true;
// Crossfade + sampling cadence. The SAMPLE interval is kept ABOVE the crossfade
// duration so a new fade never begins before the previous one has finished.
const GLOW_CROSSFADE_MS = 280;
const GLOW_SAMPLE_MS_DESKTOP = 340;
const GLOW_SAMPLE_MS_MOBILE = 420;
// Minimum colour move (squared RGB Euclidean distance) before committing a new
// crossfade — suppresses idle sampling noise so a steady sphere colour costs 0.
const GLOW_COLOR_THRESHOLD_SQ = 36;

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export default function CosmicJourney({ kicker }: { kicker: string }) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const headlineRef = useRef<HTMLDivElement | null>(null);
  const cueRef = useRef<HTMLDivElement | null>(null);
  const reducedRef = useRef(false);
  const isMobileRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    reducedRef.current = !!reduced;
  }, [reduced]);
  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  // Viewport size watcher (drives mobile vs desktop auto-scroll hand-off).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // ── Loop playback, color sampling, hand-off, and off-screen suspend ──
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return;
    const video = videoRef.current;
    const section = sectionRef.current;
    if (!video || !section) return;
    // The hero is sampled into a canvas below. Set CORS before any imperative
    // source assignment (including the Blob restore and CDN fallback).
    video.crossOrigin = "anonymous";

    // Sync the dual-buffer crossfade DOM state with this effect's initial JS
    // state (buffer A shown, both buffers on the cool-blue start color). The
    // effect re-runs when `isMobile` flips (false→true on phones right after
    // mount), and the sampler drives the --glow-* vars imperatively, so without
    // this the new closure (activeBuf="a") could disagree with stale DOM vars
    // left showing buffer B — causing a one-time color snap. Resetting both
    // buffers to the same color makes the reset invisible; the sampler then
    // crossfades to the live sphere color within one sample.
    section.style.setProperty("--glow-a", "90, 130, 255");
    section.style.setProperty("--glow-b", "90, 130, 255");
    section.style.setProperty("--glow-a-op", "1");
    section.style.setProperty("--glow-b-op", "0");

    const applyFades = (e: number) => {
      const headline = headlineRef.current;
      if (headline) {
        headline.style.opacity = clamp01(1 - e / 0.7).toFixed(3);
        headline.style.transform = `translateY(${(e * -50).toFixed(1)}px)`;
      }
      const cue = cueRef.current;
      if (cue) cue.style.opacity = clamp01(1 - e / 0.4).toFixed(3);
    };
    const restoreFades = () => {
      const headline = headlineRef.current;
      if (headline) {
        headline.style.opacity = "1";
        headline.style.transform = "translateY(0px)";
      }
      const cue = cueRef.current;
      if (cue) cue.style.opacity = "1";
    };

    // Announce readiness for the loading screen (harmless if home isn't gated).
    let announced = false;
    const announce = () => {
      if (announced) return;
      announced = true;
      try {
        window.dispatchEvent(new Event("refgd:scene-ready"));
      } catch {
        /* noop */
      }
    };
    const onLoadedData = () => announce();
    if (video.readyState >= 2) onLoadedData();
    else video.addEventListener("loadeddata", onLoadedData);

    // ── Reduced motion: one static frame, no loop, no sampling, no listeners ──
    if (reducedRef.current) {
      try {
        video.pause();
        video.currentTime = 0;
      } catch {
        /* noop */
      }
      return () => {
        video.removeEventListener("loadeddata", onLoadedData);
      };
    }

    // Safety net: if the in-memory Blob src ever fails to decode, fall back to
    // streaming the plain URL so the hero can never be stranded on a broken
    // object URL. One-shot — never loops back onto the blob.
    const onVideoError = () => {
      if (video.dataset.blobApplied !== "1") return;
      video.dataset.blobApplied = "2";
      try {
        video.crossOrigin = "anonymous";
        video.src = VIDEO_SRC_MP4;
        video.load();
        // Put the fallback source in place regardless, but never start decoding
        // in a backgrounded tab — if the blob errors while hidden, the
        // visibilitychange resume path will play() the now-set src on return.
        if (!document.hidden) {
          const p = video.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      } catch {
        /* noop */
      }
    };
    video.addEventListener("error", onVideoError);

    // ── Color sampling → dual-buffer opacity crossfade (no React re-renders) ──
    const canvas = document.createElement("canvas");
    canvas.width = 40;
    canvas.height = 24;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let cur = [90, 130, 255]; // eased current color
    let lastSample = 0;
    let sampleRaf = 0;
    let sampling = false;
    let activeBuf: "a" | "b" = "a"; // buffer currently shown (opacity 1)
    let committed = [90, 130, 255]; // color shown in the active buffer
    let crossfadeUntil = 0; // don't start a new fade until the previous one ends

    const sampleTick = (t: number) => {
      if (!sampling) return;
      sampleRaf = requestAnimationFrame(sampleTick);
      if (!ctx || video.readyState < 2 || video.paused) return;
      // Sample below the crossfade rate so fades never overlap (mobile slower).
      const interval = isMobileRef.current
        ? GLOW_SAMPLE_MS_MOBILE
        : GLOW_SAMPLE_MS_DESKTOP;
      if (t - lastSample < interval) return;
      lastSample = t;
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let rs = 0, gs = 0, bs = 0, ws = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const lum = r + g + b;
          if (lum < 70) continue; // skip the near-black background
          const w = lum * lum; // emphasize the bright vivid core
          rs += r * w; gs += g * w; bs += b * w; ws += w;
        }
        if (ws > 0) {
          let r = rs / ws, g = gs / ws, b = bs / ws;
          const avg = (r + g + b) / 3;
          const k = 1.4; // saturation boost so the edge glow reads as a real color
          r = Math.min(255, avg + (r - avg) * k);
          g = Math.min(255, avg + (g - avg) * k);
          b = Math.min(255, avg + (b - avg) * k);
          // Light easing so the committed target tracks without erratic jumps;
          // the visual smoothness comes from the opacity crossfade below.
          cur = [
            cur[0] + (r - cur[0]) * 0.5,
            cur[1] + (g - cur[1]) * 0.5,
            cur[2] + (b - cur[2]) * 0.5,
          ];
          const nr = Math.round(cur[0]);
          const ng = Math.round(cur[1]);
          const nb = Math.round(cur[2]);
          // Commit a crossfade only when the color moved enough AND the previous
          // fade has finished — a steady color costs zero blur rasters, and we
          // never stack overlapping fades.
          const dr = nr - committed[0], dg = ng - committed[1], db = nb - committed[2];
          if (dr * dr + dg * dg + db * db > GLOW_COLOR_THRESHOLD_SQ && t >= crossfadeUntil) {
            const next = activeBuf === "a" ? "b" : "a";
            // 1) paint the new color into the HIDDEN buffer (one off-screen blur
            //    raster while it is at opacity 0 — invisible, no animation), then
            // 2) crossfade by animating OPACITY only (compositor-cheap): bring
            //    the new buffer in and fade the old one out.
            section.style.setProperty(`--glow-${next}`, `${nr}, ${ng}, ${nb}`);
            section.style.setProperty(`--glow-${next}-op`, "1");
            section.style.setProperty(`--glow-${activeBuf}-op`, "0");
            activeBuf = next;
            committed = [nr, ng, nb];
            crossfadeUntil = t + GLOW_CROSSFADE_MS;
          }
        }
      } catch {
        /* sampling can briefly throw before metadata is ready */
      }
    };
    const startSampling = () => {
      // Diagnostic kill switch (see ENABLE_GLOW_SAMPLING): when off, leave the
      // glow at its static cool-blue and skip ALL readback/crossfade work.
      if (!ENABLE_GLOW_SAMPLING) return;
      // Don't churn the readback/crossfade RAF in a backgrounded tab (the video
      // is paused there anyway, so there's nothing new to sample).
      if (typeof document !== "undefined" && document.hidden) return;
      // Runs on mobile too: the dual-buffer crossfade keeps the big blurs from
      // re-rastering every frame (the old cause of phone scroll-jank), so the
      // live glow is cheap enough for phones at a lower sample cadence.
      if (sampling) return;
      sampling = true;
      sampleRaf = requestAnimationFrame(sampleTick);
    };
    const stopSampling = () => {
      sampling = false;
      cancelAnimationFrame(sampleRaf);
    };

    // Keep the sphere looping while it is on screen.
    const playLoop = () => {
      // Hold the very first network load until the loading screen has finished
      // pre-downloading the clip (see the gate below).
      if (!canLoad) return;
      // Never start/resume decoding while the tab is backgrounded. Centralised
      // here so EVERY caller (IO, openGate, handoff-end, mount kickoff, the
      // visibilitychange handler) is covered — async callers can otherwise fire
      // while document.hidden and restart hidden playback.
      if (typeof document !== "undefined" && document.hidden) return;
      // Prefer the in-memory Blob the splash captured (the EXACT bytes it
      // already downloaded). Playing from an object URL removes the dependency
      // on the browser reusing the HTTP cache for this <video>'s byte-range
      // requests — which was NOT holding on desktop or iOS, so the clip
      // re-streamed from the non-edge-cached origin and buffered. Idempotent:
      // applied at most once per element via the data flag.
      const blobUrl = (window as unknown as Record<string, unknown>)
        .__refgdHeroVideoBlobUrl;
      if (
        typeof blobUrl === "string" &&
        blobUrl &&
        !video.dataset.blobApplied &&
        video.src !== blobUrl
      ) {
        try {
          video.crossOrigin = "anonymous";
          video.src = blobUrl; // a direct src overrides the <source> child
          video.dataset.blobApplied = "1";
          video.load();
        } catch {
          /* fall back to streaming the <source> URL */
        }
      }
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    const setHeroFlight = (on: boolean) => {
      try {
        document.documentElement.classList.toggle("hero-flight", on);
      } catch {
        /* noop */
      }
    };

    const lenis = () =>
      (window as unknown as { __lenis?: { stop?: () => void; start?: () => void; scrollTo?: (t: unknown, o?: unknown) => void } }).__lenis;

    type State = "idle" | "handoff" | "done";
    let state: State = "idle";
    let handoffRaf = 0;
    let blockOn = false;
    // Hero visibility (kept current by the IntersectionObserver) + timestamp of
    // the last completed DOWN handoff. Used to (a) resume video/sampling only
    // when the hero is actually on screen, and (b) debounce an immediate
    // down->up bounce right after the down glide lands.
    let heroVisible = true;
    let doneAt = 0;

    // Capture-phase swallow of scroll input during the short auto-scroll glide.
    const swallow = (ev: Event) => {
      if (ev.cancelable) ev.preventDefault();
      ev.stopPropagation();
    };
    const blockKeys = (ev: KeyboardEvent) => {
      const k = ev.key;
      if (
        k === "ArrowDown" || k === "ArrowUp" || k === "PageDown" ||
        k === "PageUp" || k === " " || k === "Home" || k === "End"
      ) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    // `lockTouch` controls whether touchmove is swallowed on mobile during the
    // glide. The DOWN handoff passes true: the owner wants it to ALWAYS land on
    // the #paths anchor "no matter what", so it is deliberately non-interruptible
    // for its ~900ms (no NEW finger momentum can start to fight the owned tween).
    // The UP handoff passes false so a finger swipe can still interrupt it
    // (its native smooth-scroll relies on being touch-interruptible). Desktop
    // always swallows touchmove in both directions (byte-for-byte unchanged).
    const attachBlock = (lockTouch: boolean) => {
      if (blockOn) return;
      blockOn = true;
      window.addEventListener("wheel", swallow, { passive: false, capture: true });
      if (!isMobileRef.current || lockTouch) {
        window.addEventListener("touchmove", swallow, { passive: false, capture: true });
      }
      window.addEventListener("keydown", blockKeys, { capture: true });
    };
    const releaseBlock = () => {
      if (!blockOn) return;
      blockOn = false;
      window.removeEventListener("wheel", swallow, { capture: true } as EventListenerOptions);
      window.removeEventListener("touchmove", swallow, { capture: true } as EventListenerOptions);
      window.removeEventListener("keydown", blockKeys, { capture: true } as EventListenerOptions);
    };

    // Ease the welcome text out over the glide for a clean hand-off.
    const HANDOFF_MS = 900;

    const startHandoff = () => {
      if (state !== "idle") return;
      state = "handoff";
      setHeroFlight(true);
      attachBlock(true); // DOWN: lock touch so it always lands on the anchor
      // Free the GPU/main-thread during the glide: stop colour sampling so the
      // --glow writes (each of which repaints three large blurred glow layers)
      // pause, and pause the hero video so iOS isn't decoding + compositing
      // while smooth-scrolling.
      // Resumed on completion only if the hero is still on screen.
      video.pause();
      stopSampling();
      const l = lenis();
      const target = document.getElementById("paths");
      if (l && l.start) l.start();
      if (!isMobileRef.current && l && l.scrollTo && target) {
        l.scrollTo(target, {
          offset: 0,
          duration: HANDOFF_MS / 1000,
          easing: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
        });
      } else if (!isMobileRef.current && target) {
        // DESKTOP FALLBACK: Lenis was momentarily unavailable (mount race or
        // init failure). Keep the desktop landing behavior here so the mobile
        // centering branch below can NEVER run on desktop — desktop must stay
        // byte-for-byte frozen.
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (isMobileRef.current && target) {
        // MOBILE: a SINGLE native smooth scroll to the anchor. The reason this
        // now lands deterministically (and never freezes) is that we never let
        // iOS inertial momentum form in the first place: the down-handoff is
        // triggered from a touchmove handler that preventDefault()s the native
        // scroll (see the touchmove interceptor below), so the page is static
        // when this runs and the browser's own smooth scroll glides cleanly to
        // #paths with nothing to fight. We deliberately do NOT own the scroll
        // position per frame — that fought live momentum and FROZE (rounds 16 &
        // 18) — and we do NOT add a settle-snap afterwards (that was the yank).
        //
        // LANDING TARGET: BOTH the "— you have arrived" kicker (top of the
        // block) and the "Swipe to choose your door" caption (bottom) must stay
        // in frame. We land the kicker a FIXED margin below the top — we do NOT
        // center.
        //
        // Why not center: centering divided the *content height* by the viewport
        // height measured AT TRIGGER TIME. On iOS the URL bar is expanded then
        // (scrollY≈0), so the viewport is the SMALL (svh) variant and the content
        // block measured nearly as tall as it — the centering slack collapsed to
        // ~0 (or negative) and the scroll overshot PAST the kicker, clipping the
        // top. The bar then collapses during the glide (viewport grows), which is
        // why the caption ends up with spare room at the bottom while the kicker
        // is gone. A fixed top margin is immune to that drift: the kicker sits a
        // constant ~12vh from the top regardless of the measured viewport, and
        // because the viewport only grows as the bar collapses, the caption still
        // clears the bottom. Empirically ~16vh top margin clipped the caption
        // (round 19) and centering clipped the kicker (rounds 20–21); ~12vh frames
        // both. `.container-wide` wraps exactly kicker → title → carousel → dots
        // → caption, so its top is the kicker's top.
        const content = (target.querySelector(".container-wide") as HTMLElement | null) ?? target;
        const contentTop = content.getBoundingClientRect().top + window.scrollY;
        const vh = window.visualViewport?.height ?? window.innerHeight;
        const topMargin = vh * 0.12;
        window.scrollTo({ top: Math.max(0, contentTop - topMargin), behavior: "smooth" });
      }
      const t0 = performance.now();
      const drive = (now: number) => {
        const e = clamp01((now - t0) / HANDOFF_MS);
        applyFades(e);
        if (e >= 1) {
          state = "done";
          doneAt = now;
          releaseBlock();
          setHeroFlight(false);
          if (heroVisible) { playLoop(); startSampling(); }
          return;
        }
        handoffRaf = requestAnimationFrame(drive);
      };
      handoffRaf = requestAnimationFrame(drive);
    };

    // ── Reverse handoff: upward intent near the hero/#paths boundary glides
    //    all the way back to the very TOP so the visitor is never parked
    //    between the hero and the first section. Mirror of startHandoff. ──
    const startHandoffUp = () => {
      if (state !== "done") return;
      state = "handoff";
      setHeroFlight(true);
      attachBlock(false); // UP: leave touch interruptible (native smooth scroll)
      // Glide perf-freeze, UP variant: stop colour SAMPLING only (the per-write
      // blur repaints are the main cost). Do NOT pause the hero video here — the
      // hero is re-entering view, and a frozen-then-resumed sphere reads as a
      // flicker as it settles. Sampling resumes on completion.
      stopSampling();
      const l = lenis();
      if (l && l.start) l.start();
      // Mobile: use NATIVE smooth scroll, not lenis.scrollTo — a lenis rAF
      // tween isn't cancelled by a finger swipe (it fights the user for the
      // full glide = "yank, can't stop it"); native smooth scroll IS
      // interrupted by a touch-scroll. Desktop keeps lenis (byte-for-byte).
      if (!isMobileRef.current && l && l.scrollTo) {
        l.scrollTo(0, {
          offset: 0,
          duration: HANDOFF_MS / 1000,
          easing: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      const t0 = performance.now();
      const drive = (now: number) => {
        const e = clamp01((now - t0) / HANDOFF_MS);
        // Ease the welcome text back IN (1 → 0 = faded → fully visible).
        applyFades(1 - e);
        if (e >= 1) {
          state = "idle";
          releaseBlock();
          setHeroFlight(false);
          restoreFades();
          if (heroVisible) { playLoop(); startSampling(); }
          return;
        }
        handoffRaf = requestAnimationFrame(drive);
      };
      handoffRaf = requestAnimationFrame(drive);
    };

    // ── Defer the hero clip's first network load until the loading screen has
    //    finished pre-downloading the CDN sphere montage ────────────────────────
    // On a fresh full-page load the splash streams the whole clip into the HTTP
    // cache. If this <video> also fetches concurrently (autoPlay / preload) it
    // plays from its own half-finished stream and visibly re-buffers even though
    // the splash already cached the file. So we hold the first play() until the
    // splash signals its download is complete (refgd:heavy-assets-ready), at
    // which point play() reads straight from the warm cache → no buffering.
    // Opens immediately on client-side navigations (no splash) and via a safety
    // timer / loading-complete fallback so the hero can never be stranded.
    const splashActive =
      (window as unknown as Record<string, unknown>).__refgdLoadingActive ===
      true;
    // Sticky readiness flag set by <LoadingScreen> when the pre-download
    // finishes — covers the race where that completes before this effect
    // subscribes (bfcache restore / warm-cache fast resolve).
    const heavyAlreadyReady =
      (window as unknown as Record<string, unknown>).__refgdHeavyAssetsReady ===
      true;
    let canLoad = !splashActive || heavyAlreadyReady;
    let openTimer = 0;
    const openGate = () => {
      if (canLoad) return;
      canLoad = true;
      window.clearTimeout(openTimer);
      window.removeEventListener("refgd:heavy-assets-ready", openGate);
      window.removeEventListener("refgd:loading-complete", openGate);
      if (heroVisible && state !== "handoff") {
        playLoop();
        startSampling();
      }
    };
    if (!canLoad) {
      window.addEventListener("refgd:heavy-assets-ready", openGate);
      window.addEventListener("refgd:loading-complete", openGate);
      // Last-resort backstop: never leave the hero on a static poster if both
      // signals are somehow missed (e.g. splash disabled mid-session). Set ABOVE
      // the splash's 30s hard ceiling so this can never fire mid-download and
      // re-introduce the concurrent fetch this gate exists to prevent — by 32s
      // the splash has already lifted and dispatched refgd:loading-complete.
      openTimer = window.setTimeout(openGate, 32000);
    }

    // ── Off-screen suspend (perf): stop loop AND sampling when hero leaves ──
    const io = new IntersectionObserver(
      ([entry]) => {
        heroVisible = entry.isIntersecting;
        // Don't fight an in-progress glide: the handoff owns play/pause during
        // the flight and resumes once the hero has settled. Without this guard
        // the IO would un-pause the video the instant the hero re-enters view
        // on the UP glide, defeating the perf-freeze.
        if (state === "handoff") return;
        if (entry.isIntersecting) {
          playLoop();
          startSampling();
        } else {
          video.pause();
          stopSampling();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(section);

    // ── Tab-away suspend (perf + battery) ──
    // The IntersectionObserver above only reacts to the hero leaving the
    // VIEWPORT — it does NOT fire when the visitor switches to another browser
    // tab (the element is still laid out on-screen, just on a backgrounded
    // page). Without this the sphere <video> + colour sampler keep decoding in
    // the hidden tab. Mirror the off-screen suspend on document visibility:
    // pause + stop sampling when hidden, and resume only if the gate is open,
    // the hero is still on-screen, and we're not mid-handoff.
    const onTabVisibility = () => {
      if (document.hidden) {
        video.pause();
        stopSampling();
      } else if (canLoad && heroVisible && state !== "handoff") {
        playLoop();
        startSampling();
      }
    };
    document.addEventListener("visibilitychange", onTabVisibility);

    // Kick things off immediately (the IO callback also fires shortly after).
    playLoop();
    startSampling();

    // ── Scroll snap — works on BOTH mobile (scroll events) and desktop ──
    // Fires on every native scroll: down from top → glide to #paths;
    // up near the boundary → glide back to top. Never yanks from deep content.
    const atTop = () => window.scrollY <= 2;
    const nearBoundary = () => window.scrollY <= window.innerHeight * 1.15;
    // Mobile: the up-handoff must commit to the very top as soon as the hero
    // starts re-appearing, so the visitor can't rest "half hero / half cards".
    // #paths starts ~1 viewport down, so any scrollY below ~0.85vh means a
    // strip of hero is showing while going up -> glide home. Kept just under
    // 1.0vh (not at it) so the down-handoff's landing point can't instantly
    // bounce back up (paired with the doneAt cooldown below); 0.85 also clears
    // the 100svh-vs-innerHeight gap on iOS. Deep card browsing (scrollY >=
    // ~1.0vh) is never touched. Desktop keeps 1.15 so the wheel/keyboard
    // handoff stays byte-for-byte unchanged.
    const nearBoundaryUp = () =>
      window.scrollY <= window.innerHeight * (isMobileRef.current ? 0.85 : 1.15);
    let prevY = window.scrollY;
    const onScrollTrigger = () => {
      const y = window.scrollY;
      const goingDown = y > prevY;
      const goingUp = y < prevY;
      const wasAtTop = prevY <= 2;
      prevY = y;
      if (
        // DESKTOP ONLY: on mobile the down-handoff starts exclusively from the
        // touchmove interceptor (which preventDefault()s native scroll so no iOS
        // momentum forms). Letting this scroll-based branch fire on mobile would
        // bypass that and reintroduce the variable/cut-off landing.
        !isMobileRef.current &&
        state === "idle" && goingDown && wasAtTop && y > 2 && y < window.innerHeight * 0.6
      ) {
        startHandoff();
      } else if (
        state === "done" && goingUp && nearBoundaryUp() &&
        performance.now() - doneAt > 400
      ) {
        startHandoffUp();
      }
    };
    // Re-arm when the user returns to the very top manually.
    const onScrollReset = () => {
      if (state === "done" && window.scrollY < 8) {
        state = "idle";
        cancelAnimationFrame(handoffRaf);
        restoreFades();
        setHeroFlight(false);
      }
    };

    // Auto-scroll handoff runs on BOTH mobile and desktop via native scroll
    // events (down from the top -> glide to #paths; up near the boundary ->
    // glide back to the top). Desktop adds precise wheel/keyboard triggers below.
    window.addEventListener("scroll", onScrollTrigger, { passive: true });
    window.addEventListener("scroll", onScrollReset, { passive: true });

    // Track whether a finger is on the glass + where the touch began, so the
    // interceptor below can tell a downward (scroll-down) drag from an upward one.
    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchEnd = () => {};
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    // ── MOBILE down-handoff interceptor (the real fix) ──
    // Every prior round tried to FIGHT or KILL iOS inertial momentum after the
    // flick already started: owning the scroll per-frame froze (it fought live
    // momentum), and a single native smooth scroll landed at a variable, cut-off
    // spot (momentum overrode it). The cure is to never let momentum form: while
    // we're at the very top and idle, the first downward drag is preventDefault()ed
    // here (non-passive) so the browser performs NO native scroll, then we trigger
    // the glide. With no native scroll there is no inertia, so the smooth scroll in
    // startHandoff lands EXACTLY on #paths every time — no freeze, no yank.
    // Mobile-only; desktop keeps its wheel/keyboard triggers untouched.
    const onTouchMoveTrigger = (e: TouchEvent) => {
      if (!isMobileRef.current || state !== "idle") return;
      if (e.touches.length !== 1) return; // ignore pinch / multi-touch gestures
      if (window.scrollY > 2) return; // only intercept from the very top
      const dy = touchStartY - (e.touches[0]?.clientY ?? touchStartY);
      if (dy > 6) {
        // finger moving up the glass => scroll-DOWN intent: block native scroll
        e.preventDefault();
        startHandoff();
      }
    };
    window.addEventListener("touchmove", onTouchMoveTrigger, { passive: false });

    // ── Desktop only: precise wheel/keyboard triggers on top of scroll ──
    let cleanupDesktop = () => {};
    if (!isMobileRef.current) {
      const onWheel = (ev: WheelEvent) => {
        if (state === "idle" && ev.deltaY > 0 && atTop()) {
          if (ev.cancelable) ev.preventDefault();
          startHandoff();
        } else if (state === "done" && ev.deltaY < 0 && nearBoundary()) {
          if (ev.cancelable) ev.preventDefault();
          startHandoffUp();
        }
      };
      const onKey = (ev: KeyboardEvent) => {
        if (
          state === "idle" && atTop() &&
          (ev.key === "ArrowDown" || ev.key === "PageDown" || ev.key === " " || ev.key === "End")
        ) {
          ev.preventDefault();
          startHandoff();
        } else if (
          state === "done" && nearBoundary() &&
          (ev.key === "ArrowUp" || ev.key === "PageUp" || ev.key === "Home")
        ) {
          ev.preventDefault();
          startHandoffUp();
        }
      };
      window.addEventListener("wheel", onWheel, { passive: false });
      window.addEventListener("keydown", onKey);
      cleanupDesktop = () => {
        window.removeEventListener("wheel", onWheel);
        window.removeEventListener("keydown", onKey);
      };
    }

    let cleanupTriggers = () => {
      window.removeEventListener("scroll", onScrollTrigger);
      window.removeEventListener("scroll", onScrollReset);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("touchmove", onTouchMoveTrigger);
      cleanupDesktop();
    };

    return () => {
      cancelAnimationFrame(handoffRaf);
      stopSampling();
      releaseBlock();
      cleanupTriggers();
      io.disconnect();
      document.removeEventListener("visibilitychange", onTabVisibility);
      window.clearTimeout(openTimer);
      window.removeEventListener("refgd:heavy-assets-ready", openGate);
      window.removeEventListener("refgd:loading-complete", openGate);
      video.removeEventListener("loadeddata", onLoadedData);
      video.removeEventListener("error", onVideoError);
      const l = lenis();
      if (l && l.start) l.start();
      setHeroFlight(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, reduced, isMobile]);

  return (
    <section
      ref={sectionRef}
      data-testid="cosmic-journey"
      data-hero-build="glow-edge-boost-24"
      className="relative w-full overflow-hidden"
      style={{
        height: "100svh",
        // Dual-buffer glow colours + their crossfade opacities. Both buffers
        // start on the same cool blue; A is shown, B is hidden.
        ["--glow-a" as string]: "90, 130, 255",
        ["--glow-b" as string]: "90, 130, 255",
        ["--glow-a-op" as string]: "1",
        ["--glow-b-op" as string]: "0",
      }}
    >
      {/* Solid near-black backdrop so the hero is never blank before the first
          frame paints. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "#05060a" }}
      />

      {/* Ambient glow behind the sphere — two crossfade buffers (A/B). Each is
          identical except for its colour var + opacity var; the sampler paints
          the new colour into the hidden one and fades the opacities. */}
      <div
        aria-hidden="true"
        className="cj-glow-ambient cj-glow-ambient-a pointer-events-none absolute left-1/2 top-1/2"
        style={{
          width: "72vmin",
          height: "72vmin",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(var(--glow-a), 0.55), rgba(var(--glow-a), 0.12) 45%, transparent 68%)",
          filter: "blur(70px)",
          opacity: "var(--glow-a-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />
      <div
        aria-hidden="true"
        className="cj-glow-ambient cj-glow-ambient-b pointer-events-none absolute left-1/2 top-1/2"
        style={{
          width: "72vmin",
          height: "72vmin",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(var(--glow-b), 0.55), rgba(var(--glow-b), 0.12) 45%, transparent 68%)",
          filter: "blur(70px)",
          opacity: "var(--glow-b-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />

      {/* ── Looping sphere clip ── */}
      <video
        ref={videoRef}
        aria-hidden="true"
        className="cj-hero-video absolute left-1/2 top-1/2"
        crossOrigin="anonymous"
        muted
        loop={!reduced}
        playsInline
        preload="none"
        poster="/sphere-poster.webp"
        style={{
          width: "100%",
          transform: "translate(-50%, -50%)",
          objectFit: "cover",
          display: "block",
        }}
      >
        <source src={VIDEO_SRC_MP4} type="video/mp4" />
      </video>

      {/* MOBILE-only painted edge vignette (.cj-hero-vignette). Fades the video
          rectangle into the #05060a hero backdrop with a plain background
          gradient iOS always honours, doing the job the unreliable <video>
          mask can't. Hidden on desktop, where the mask handles the edges. */}
      <div
        aria-hidden="true"
        className="cj-hero-vignette pointer-events-none absolute inset-0"
      />

      {/* Dark scrim behind the text for white-text legibility. The mobile vs
          desktop gradients live in CSS (.cj-hero-scrim) — NOT a React isMobile
          flag — so phones paint the soft mobile wash on the very first frame
          instead of briefly showing the desktop hard-edged oval. Rendered
          before the colored edges so the edge glow paints on top. */}
      <div
        aria-hidden="true"
        className="cj-hero-scrim pointer-events-none absolute inset-0"
      />

      {/* Soft blurred gradient EDGES, tinted live to the sphere color. Wide,
          strong band so the color is clearly visible around the whole frame.
          Two crossfade buffers (A/B) — see the ambient glow note above. */}
      <div
        aria-hidden="true"
        className="cj-glow-edge cj-glow-edge-a pointer-events-none absolute"
        style={{
          inset: "-15%",
          background:
            "radial-gradient(78% 78% at 50% 50%, transparent 18%, rgba(var(--glow-a), 0.45) 40%, rgba(var(--glow-a), 0.8) 62%, rgba(var(--glow-a), 1) 80%)",
          filter: "blur(60px)",
          mixBlendMode: "screen",
          opacity: "var(--glow-a-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />
      <div
        aria-hidden="true"
        className="cj-glow-edge cj-glow-edge-b pointer-events-none absolute"
        style={{
          inset: "-15%",
          background:
            "radial-gradient(78% 78% at 50% 50%, transparent 18%, rgba(var(--glow-b), 0.45) 40%, rgba(var(--glow-b), 0.8) 62%, rgba(var(--glow-b), 1) 80%)",
          filter: "blur(60px)",
          mixBlendMode: "screen",
          opacity: "var(--glow-b-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />

      {/* Extra CORNER glow — four radial pools anchored to the VIEWPORT corners
          (10%/90% of the -15% overscan div ≈ each screen corner) so their bright
          cores stay INSIDE the frame instead of being thrown away by the hero's
          overflow-hidden. Two crossfade buffers (A/B) — see the ambient note. */}
      <div
        aria-hidden="true"
        className="cj-glow-corner cj-glow-corner-a pointer-events-none absolute"
        style={{
          inset: "-15%",
          background:
            "radial-gradient(58% 58% at 10% 10%, rgba(var(--glow-a), 1) 0%, rgba(var(--glow-a), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 90% 10%, rgba(var(--glow-a), 1) 0%, rgba(var(--glow-a), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 10% 90%, rgba(var(--glow-a), 1) 0%, rgba(var(--glow-a), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 90% 90%, rgba(var(--glow-a), 1) 0%, rgba(var(--glow-a), 0.55) 34%, transparent 72%)",
          filter: "blur(48px)",
          mixBlendMode: "screen",
          opacity: "var(--glow-a-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />
      <div
        aria-hidden="true"
        className="cj-glow-corner cj-glow-corner-b pointer-events-none absolute"
        style={{
          inset: "-15%",
          background:
            "radial-gradient(58% 58% at 10% 10%, rgba(var(--glow-b), 1) 0%, rgba(var(--glow-b), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 90% 10%, rgba(var(--glow-b), 1) 0%, rgba(var(--glow-b), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 10% 90%, rgba(var(--glow-b), 1) 0%, rgba(var(--glow-b), 0.55) 34%, transparent 72%), radial-gradient(58% 58% at 90% 90%, rgba(var(--glow-b), 1) 0%, rgba(var(--glow-b), 0.55) 34%, transparent 72%)",
          filter: "blur(48px)",
          mixBlendMode: "screen",
          opacity: "var(--glow-b-op)",
          transition: `opacity ${GLOW_CROSSFADE_MS}ms linear`,
          willChange: "opacity",
        }}
      />

      <div className="absolute inset-0 grid place-items-center">
        {/* ── WELCOME headline ── */}
        <motion.div
          ref={headlineRef}
          className="container-wide pointer-events-none relative z-[5] flex flex-col items-center justify-center text-center"
          initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 28, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={reduced ? { duration: 0 } : { duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          <KineticText
            as="h1"
            text={kicker}
            className="editorial-display text-balance uppercase text-white text-[clamp(2.5rem,9vw,7rem)] leading-[0.95] tracking-[-0.015em]"
            style={{ textShadow: "0 4px 50px rgba(0,0,0,0.95), 0 0 60px rgba(245,185,69,0.45), 0 2px 14px rgba(0,0,0,0.95)" }}
            stagger={0.08}
            delay={0.15}
          />
        </motion.div>
      </div>

      {/* ── Bold, unmissable scroll cue ── */}
      <div
        ref={cueRef}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-10 left-1/2 z-[6] flex -translate-x-1/2 flex-col items-center gap-3 text-white"
        style={{ opacity: 1 }}
      >
        <style>{`
          @keyframes cj-cue-bounce {
            0%, 100% { transform: translateY(0); opacity: 1; }
            50%      { transform: translateY(7px); opacity: 0.55; }
          }
          @media (prefers-reduced-motion: reduce) {
            .cj-cue-chevron { animation: none !important; }
          }
        `}</style>
        <span
          className="heading-display rounded-full border border-amber-200/40 bg-black/30 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.4em] backdrop-blur-sm sm:text-sm"
          style={{ textShadow: "0 2px 14px rgba(0,0,0,0.95), 0 0 22px rgba(255,237,180,0.55)" }}
        >
          Scroll to begin
        </span>
        <svg
          className="cj-cue-chevron h-6 w-6 text-amber-200"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{ animation: "cj-cue-bounce 1.6s ease-in-out infinite", filter: "drop-shadow(0 0 8px rgba(255,237,180,0.7))" }}
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </section>
  );
}
